/**
 * Master Import API Service
 * Handles all API calls for the master tag import feature.
 */

import HttpService from '@/utils/httpService';
import { $getFromLocal } from '@/utils';
import appConfig from '../../../../appConfig';

const BASE_URL = 'api/master-imports';

export default {
  /**
   * Create an empty batch (used when setting up an API data source before any import).
   * @param {Object} request - { targetType, targetId, tagType, batchName }
   * @returns {Promise<{ id, batchName, tagType, ... }>}
   */
  async createEmptyBatch(request) {
    const response = await HttpService.post({ url: `${BASE_URL}/batches`, data: request });
    return response.data || response;
  },

  /**
   * Import master tags from Excel data.
   * @param {Object} request - ImportMasterTagsRequest
   * @returns {Promise<ImportResult>}
   */
  async importMasterTags(request) {
    const response = await HttpService.post({ url: BASE_URL, data: request });
    return response.data || response;
  },

  /**
   * Check for duplicate tag names before import.
   * @param {Object} request - { targetType, targetId, tagType, tagNames }
   * @returns {Promise<DuplicateCheckResult>}
   */
  async checkDuplicates(request) {
    const response = await HttpService.post({ url: `${BASE_URL}/check-duplicates`, data: request });
    return response.data || response;
  },

  /**
   * Get paginated list of import batches.
   * @param {Object} params - { facilityId, facilityGroupId, tagType, page, itemsPerPage, sortBy, sortDesc }
   * @returns {Promise<PagedResult<ImportBatchInfo>>}
   */
  async getBatches(params) {
    const response = await HttpService.get({ url: `${BASE_URL}/batches`, params });
    return response.data || response;
  },

  /**
   * Get single batch with schema definition.
   * @param {string} batchId
   * @returns {Promise<MasterImportBatch>}
   */
  async getBatch(batchId, options = {}) {
    const response = await HttpService.get({ url: `${BASE_URL}/batches/${batchId}`, ...options });
    return response.data || response;
  },

  /**
   * Get paginated tags from a specific batch.
   * @param {string} batchId
   * @param {Object} pagination - { page, itemsPerPage, sortBy, sortDesc }
   * @returns {Promise<PagedResult<MasterTagDto>>}
   */
  async getBatchTags(batchId, pagination = {}, options = {}) {
    const response = await HttpService.get({
      url: `${BASE_URL}/batches/${batchId}/tags`,
      params: pagination,
      ...options,
    });
    return response.data || response;
  },

  /**
   * Delete a batch (hard delete batch + unharvested tags, orphan harvested tags).
   * @param {string} batchId
   * @returns {Promise<{ success: boolean, message: string }>}
   */
  async deleteBatch(batchId) {
    const response = await HttpService.delete({ url: `${BASE_URL}/batches/${batchId}` });
    return response.data || response;
  },

  /**
   * Append records to an existing batch.
   * @param {string} batchId - The batch to append to
   * @param {Object} request - AppendToBatchRequest
   * @param {Array} request.records - Records to append (systemFields + customFields)
   * @param {string} request.duplicateHandling - 'Skip' | 'Merge'
   * @param {string} [request.sourceFileName] - Source file name for audit
   * @param {Array} [request.schemaExtension] - New columns to add to schema (if merge selected)
   * @returns {Promise<AppendResult>}
   */
  async appendToBatch(batchId, request) {
    const response = await HttpService.post({
      url: `${BASE_URL}/batches/${batchId}/append`,
      data: request,
    });
    return response.data || response;
  },

  /**
   * Get all custom attribute schemas for a facility.
   * Returns a dictionary of batch IDs to schema definitions.
   * Used for caching schemas at facility initialization.
   * @param {string} facilityId
   * @returns {Promise<Object<string, RegisterColumnDefinition[]>>}
   */
  async getAllSchemas(facilityId, config = {}) {
    const response = await HttpService.get({
      url: `${BASE_URL}/schemas`,
      params: { facilityId },
      ...config,
    });
    return response.data || response;
  },

  /**
   * Import master tags with SSE streaming for real-time progress updates.
   * Uses native fetch + ReadableStream since axios doesn't support SSE.
   *
   * @param {Object} request - ImportMasterTagsRequest (same as importMasterTags)
   * @param {Function} onProgress - Callback for progress events: (progress) => void
   *   Progress shape: { processed, total, successCount, skippedCount, mergedCount, failedCount }
   * @returns {Promise<ImportResult>} Final import result
   */
  async importMasterTagsStream(request, onProgress) {
    // Use same token retrieval as HttpService for consistency
    const token = $getFromLocal('accessToken', false, null);
    const apiUrl = appConfig.API_URL.replace(/\/$/, ''); // Remove trailing slash

    const response = await fetch(`${apiUrl}/${BASE_URL}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Import failed: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch {
        if (errorText) errorMessage = errorText;
      }
      throw new Error(errorMessage);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;
    let nextIsComplete = false;
    let nextIsError = false;
    let done = false;

    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (done) break;
      const value = result.value;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // Keep incomplete line in buffer

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        if (trimmedLine.startsWith('event: complete')) {
          nextIsComplete = true;
        } else if (trimmedLine.startsWith('event: error')) {
          nextIsError = true;
        } else if (trimmedLine.startsWith('data: ')) {
          try {
            const json = JSON.parse(trimmedLine.slice(6));

            if (nextIsError) {
              throw new Error(json.error || 'Import failed');
            }

            if (nextIsComplete) {
              finalResult = json;
              nextIsComplete = false;
            } else {
              // Progress event
              if (onProgress && typeof onProgress === 'function') {
                onProgress(json);
              }
            }
          } catch (parseErr) {
            if (nextIsError) throw parseErr;
            // Ignore parse errors for progress events
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const trimmedLine = buffer.trim();
      if (trimmedLine.startsWith('data: ')) {
        try {
          const json = JSON.parse(trimmedLine.slice(6));
          if (nextIsComplete) {
            finalResult = json;
          }
        } catch {
          // Ignore
        }
      }
    }

    if (!finalResult) {
      throw new Error('Import stream ended without a complete event');
    }

    return finalResult;
  },
};
